/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-28
 */

import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Button,
} from '@mui/material';
import { useLocation } from 'react-router-dom';

// Add local dependencies.
import { useGetUserStudiosQuery } from '../../../features/studios/studiosApi';
import { StudioRole } from '../../../types/studio';
import Header from '../../layout/Header';
import NoStudiosView from './NoStudiosView';
import SingleStudioView from './SingleStudioView';
import MultipleStudiosView from './MultipleStudiosView';

/**
 * Studios page component.
 * Shows different views based on the number of studios the user belongs to:
 * - 0 studios: NoStudiosView with onboarding
 * - 1 studio: SingleStudioView with prominent display
 * - Multiple studios: MultipleStudiosView with list and indicators
 *
 * @example
 * return (
 *   <Studios />
 * )
 * @returns {React.ReactElement} component.
 */
const Studios: React.FC = () => {
  const location = useLocation();

  // Check if we were redirected here due to insufficient tool permissions
  const toolAccessDenied = (location.state as any)?.toolAccessDenied;

  // Fetch user's studios to determine which view to show
  const {
    data: studiosData,
    isLoading,
    error,
    refetch,
  } = useGetUserStudiosQuery();

  const studioCount = studiosData?.total || 0;

  // Use the user role data from the API response
  const studiosWithRoles = (studiosData?.studios || []).map(studio => {
    // Get the user role from the API response, default to MEMBER if not available
    const userRole = (studio as any).user_role || StudioRole.MEMBER;

    console.log(
      'Studio:',
      studio.name,
      'API role:',
      (studio as any).user_role,
      'Mapped role:',
      userRole
    );

    return {
      ...studio,
      userRole,
    };
  });

  const studios = studiosWithRoles;

  // Helper function to format required roles
  const formatRoles = (roles: string[]): string => {
    const formatted = roles.map(role => {
      switch (role) {
        case 'owner':
          return 'Owner';
        case 'manager':
          return 'Manager';
        case 'admin':
          return 'Admin';
        default:
          return role;
      }
    });

    if (formatted.length === 1) {
      return formatted[0];
    }
    if (formatted.length === 2) {
      return `${formatted[0]} or ${formatted[1]}`;
    }
    const lastRole = formatted.pop();
    return `${formatted.join(', ')}, or ${lastRole}`;
  };

  // Show loading state while fetching studios
  if (isLoading) {
    return (
      <>
        <Header />
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="50vh"
          gap={2}
        >
          <CircularProgress size={50} />
          <Typography variant="h6" color="text.secondary">
            Loading studios...
          </Typography>
        </Box>
      </>
    );
  }

  // Show error state if there's an error loading studios
  if (error) {
    console.error('Studios API Error:', error);

    return (
      <>
        <Header />
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="50vh"
          gap={2}
          sx={{ p: 3 }}
        >
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Failed to load studios
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {error && 'status' in error
                ? `API Error: ${error.status} - ${error.data || 'Unknown error'}`
                : 'Network or connection error'}
            </Typography>
            <Typography variant="body2">
              You are authenticated, but there was an issue loading your
              studios.
            </Typography>
          </Alert>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={() => refetch()}>
              Try Again
            </Button>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </Box>
        </Box>
      </>
    );
  }

  // Render appropriate view based on studio count
  return (
    <>
      <Header />

      {/* Tool Access Denied Message */}
      {toolAccessDenied && (
        <Box sx={{ maxWidth: 'lg', mx: 'auto', px: 3, pt: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              <strong>
                The {toolAccessDenied.toolName} tool requires{' '}
                {formatRoles(toolAccessDenied.requiredRoles)} permissions.
              </strong>
            </Typography>
            <Typography variant="body2">
              You currently don&apos;t have any studios where you have these
              permissions. Contact your studio owner or manager for elevated
              permissions, or create your own studio.
            </Typography>
          </Alert>
        </Box>
      )}

      {studioCount === 0 && <NoStudiosView />}
      {studioCount === 1 && (
        <SingleStudioView
          studio={studios[0]}
          hasNotifications={false} // TODO: Add notification logic
          lastActivity={studios[0]?.updatedAt}
        />
      )}
      {studioCount > 1 && (
        <MultipleStudiosView
          studios={studios}
          totalCount={studioCount}
          onRefresh={refetch}
        />
      )}
    </>
  );
};

export default Studios;
